import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import db from '../services/db';
import { normalizeAccountStatus } from '../utils/userLifecycle';
import { getUserRole, isPortalUser } from '../utils/userRole';

const AuthContext = createContext(null);

const PORTAL_LOGIN_HINT = 'Этот аккаунт в Портале. Войдите на странице /portal/login.';

async function buildFullUser(sessionUser, profile) {
    const status = normalizeAccountStatus(profile || {});
    const authEmail = sessionUser?.email?.trim() || '';
    const profileEmail = profile?.email?.trim() || '';
    const email = profileEmail || authEmail;
    let fullUser = { ...sessionUser, ...(profile || {}), status, email };

    if (getUserRole(fullUser) === 'portal') {
        const account = await db.getPortalAccount(sessionUser.id);
        fullUser = {
            ...fullUser,
            tokenBalance: account?.token_balance ?? 0,
        };
    }

    return fullUser;
}

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const applySessionProfile = useCallback(async (sessionUser, profile) => {
        if (!profile) {
            setUser(sessionUser);
            return sessionUser;
        }

        const status = normalizeAccountStatus(profile);
        if (status === 'Deactivated') {
            await db.supabase.auth.signOut();
            localStorage.removeItem('lex_profile');
            setUser(null);
            return null;
        }

        const fullUser = await buildFullUser(sessionUser, profile);
        setUser(fullUser);
        localStorage.setItem('lex_profile', JSON.stringify(fullUser));
        return fullUser;
    }, []);

    const loadProfileForSession = useCallback(async (sessionUser) => {
        const q = db.supabase
            .from('profiles')
            .select('*')
            .eq('id', sessionUser.id)
            .single();

        const { data: profile } = await db._withTimeout(q, 30000, 'auth:profile');
        if (!profile) {
            setUser(sessionUser);
            return sessionUser;
        }

        return applySessionProfile(sessionUser, profile);
    }, [applySessionProfile]);

    useEffect(() => {
        const cached = localStorage.getItem('lex_profile');
        if (cached) {
            try {
                setUser(JSON.parse(cached));
            } catch (e) {
                /* ignore */
            }
        }

        const initAuth = async () => {
            try {
                const sessionQuery = db.supabase.auth.getSession();
                const { data: { session } } = await db._withTimeout(sessionQuery, 6000, 'auth:getSession');
                if (!session?.user) {
                    localStorage.removeItem('lex_profile');
                    setUser(null);
                    return;
                }
                try {
                    await loadProfileForSession(session.user);
                } catch (err) {
                    console.error('[useAuth] Profile fetch failed/slow:', err.message);
                    setUser(session.user);
                }
            } catch (err) {
                console.error('[useAuth] Session check failed:', err.message);
            } finally {
                setLoading(false);
            }
        };

        initAuth();

        const { data: { subscription } } = db.supabase.auth.onAuthStateChange(async (event, session) => {
            if (event === 'SIGNED_OUT') {
                setUser(null);
                localStorage.removeItem('lex_profile');
            } else if (session?.user) {
                const current = JSON.parse(localStorage.getItem('lex_profile') || 'null');
                if (!current || current.id !== session.user.id) {
                    try {
                        await loadProfileForSession(session.user);
                    } catch (err) {
                        console.error('[useAuth] Profile fetch error:', err);
                        setUser(session.user);
                    }
                }
            }
        });

        return () => subscription.unsubscribe();
    }, [loadProfileForSession]);

    const login = async (username, password) => {
        try {
            const normalizedUsername = username.trim().toLowerCase();
            const academyEmail = `${normalizedUsername}@learning.site`;

            const attemptSignIn = (email) => {
                const q = db.supabase.auth.signInWithPassword({ email, password });
                return db._withTimeout(q, 30000, 'login');
            };

            let { data, error } = await attemptSignIn(academyEmail);

            // Legacy migrated accounts may have bare username stored as auth email (no @learning.site).
            if (error && normalizedUsername !== academyEmail && /invalid login credentials/i.test(error.message || '')) {
                ({ data, error } = await attemptSignIn(normalizedUsername));
            }

            if (error) throw error;

            const pq = db.supabase
                .from('profiles')
                .select('*')
                .eq('id', data.user.id)
                .single();
            const { data: profile } = await db._withTimeout(pq, 30000, 'login:profile');

            const role = getUserRole(profile || data.user) || 'student';

            if (role === 'portal') {
                await db.supabase.auth.signOut();
                localStorage.removeItem('lex_profile');
                return { success: false, code: 'USE_PORTAL_LOGIN', message: PORTAL_LOGIN_HINT };
            }

            const status = normalizeAccountStatus(profile || {});

            if (status === 'Deactivated') {
                await db.supabase.auth.signOut();
                localStorage.removeItem('lex_profile');
                return {
                    success: false,
                    code: 'ACCOUNT_DEACTIVATED',
                    message: 'Аккаунт деактивирован. Свяжитесь с преподавателем, если считаете это ошибкой.',
                };
            }

            if (profile) {
                const fullUser = await buildFullUser(data.user, profile);
                setUser(fullUser);
                localStorage.setItem('lex_profile', JSON.stringify(fullUser));
            }

            return { success: true, role };
        } catch (error) {
            console.error('Login failed:', error.message);
            return { success: false, message: error.message };
        }
    };

    const loginPortal = async (email, password) => {
        try {
            const normalized = email.trim().toLowerCase();
            if (!normalized.includes('@')) {
                return { success: false, message: 'Укажите корректный адрес email.' };
            }

            const q = db.supabase.auth.signInWithPassword({
                email: normalized,
                password,
            });

            const { data, error } = await db._withTimeout(q, 30000, 'loginPortal');

            if (error) throw error;

            const pq = db.supabase
                .from('profiles')
                .select('*')
                .eq('id', data.user.id)
                .single();
            const { data: profile } = await db._withTimeout(pq, 30000, 'loginPortal:profile');

            const role = getUserRole(profile || data.user);

            if (role !== 'portal') {
                await db.supabase.auth.signOut();
                localStorage.removeItem('lex_profile');
                return {
                    success: false,
                    message: 'Для учеников и преподавателей используйте вход на сайте академии.',
                };
            }

            const status = normalizeAccountStatus(profile || {});
            if (status === 'Deactivated') {
                await db.supabase.auth.signOut();
                localStorage.removeItem('lex_profile');
                return {
                    success: false,
                    message: 'Аккаунт Портала деактивирован. Напишите нам, если нужна помощь.',
                };
            }

            const fullUser = await buildFullUser(data.user, profile);
            setUser(fullUser);
            localStorage.setItem('lex_profile', JSON.stringify(fullUser));

            return { success: true };
        } catch (error) {
            console.error('Portal login failed:', error.message);
            const msg = error.message || 'Не удалось войти.';
            if (/email not confirmed/i.test(msg)) {
                return {
                    success: false,
                    code: 'EMAIL_NOT_CONFIRMED',
                    message: 'Подтвердите email по ссылке из письма, затем войдите снова.',
                };
            }
            return { success: false, message: msg };
        }
    };

    const registerPortal = async ({ email, password, name }) => {
        try {
            const normalized = email.trim().toLowerCase();
            if (!normalized.includes('@')) {
                return { success: false, message: 'Укажите корректный адрес email.' };
            }
            if (normalized.endsWith('@learning.site')) {
                return {
                    success: false,
                    message: 'Аккаунты академии создаёт преподаватель. Для Портала используйте личный email.',
                };
            }
            if (!password || password.length < 8) {
                return { success: false, message: 'Пароль должен быть не короче 8 символов.' };
            }

            const q = db.supabase.auth.signUp({
                email: normalized,
                password,
                options: {
                    data: {
                        role: 'portal',
                        name: (name || '').trim(),
                    },
                    emailRedirectTo: `${window.location.origin}/portal/login?verified=1`,
                },
            });

            const { data, error } = await db._withTimeout(q, 30000, 'registerPortal');

            if (error) throw error;

            const needsEmailConfirmation = !data.session;

            if (data.session?.user) {
                await new Promise((r) => setTimeout(r, 400));
                await loadProfileForSession(data.session.user);
            }

            return { success: true, needsEmailConfirmation };
        } catch (error) {
            console.error('Portal registration failed:', error.message);
            return { success: false, message: error.message || 'Не удалось зарегистрироваться.' };
        }
    };

    const logout = async () => {
        await db.supabase.auth.signOut();
        setUser(null);
        localStorage.removeItem('lex_profile');
    };

    const isAdmin = user?.role === 'teacher' || user?.user_metadata?.role === 'teacher';
    const isPortal = isPortalUser(user);

    const refreshPortalBalance = useCallback(async () => {
        if (!user?.id || !isPortalUser(user)) return;
        const account = await db.getPortalAccount(user.id);
        setUser((prev) => {
            if (!prev) return prev;
            const next = { ...prev, tokenBalance: account?.token_balance ?? 0 };
            localStorage.setItem('lex_profile', JSON.stringify(next));
            return next;
        });
    }, [user?.id, user?.role]);

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                login,
                loginPortal,
                registerPortal,
                logout,
                isAdmin,
                isPortal,
                refreshPortalBalance,
            }}
        >
            {loading ? (
                <div className="splash-screen">
                    <div className="splash-content">
                        <div className="splash-logo">🌿</div>
                        <div className="splash-text">АлексКласс</div>
                        <div className="loading-dots">
                            <span></span><span></span><span></span>
                        </div>
                    </div>
                    <style>{`
                        .splash-screen {
                            position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                            background: white; display: flex; align-items: center; justify-content: center;
                            z-index: 9999;
                        }
                        .splash-content { text-align: center; }
                        .splash-logo { font-size: 4rem; margin-bottom: 1rem; animation: pulse 2s infinite ease-in-out; }
                        .splash-text { font-family: var(--font-family, 'Ubuntu', sans-serif); font-size: 1.5rem; font-weight: 800; color: #1e293b; letter-spacing: -0.02em; }
                        @keyframes pulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.1); opacity: 0.8; } }
                        .loading-dots { display: flex; gap: 6px; justify-content: center; margin-top: 1.5rem; }
                        .loading-dots span { width: 8px; height: 8px; background: #4361ee; border-radius: 50%; animation: dot-bounce 1.4s infinite ease-in-out both; }
                        .loading-dots span:nth-child(1) { animation-delay: -0.32s; }
                        .loading-dots span:nth-child(2) { animation-delay: -0.16s; }
                        @keyframes dot-bounce { 0%, 80%, 100% { transform: scale(0); } 40% { transform: scale(1); } }
                    `}</style>
                </div>
            ) : children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
